import React, { Component } from "react";
import { push } from "connected-react-router";
import { ContentWrapper, Icon, PhoneInput, Table } from "components";
import { Row, Col, Input, notification, Drawer } from "antd";
import { Helmet } from "react-helmet";
import { connect } from "react-redux";
import { delayedDispatch, setBreadcrumb, setLoader, setTitle, updateCrumb } from "store/actions";
import { API, Endpoints } from "utils/api";
import { emailIsValid } from "utils/utils";
import Strings from "utils/strings";
import { translate } from "utils/utils";
import { DateTime } from "luxon";
import "./styles.scss";

class SupplierDetail extends Component<any, any> {
	phoneValidationInput: any;

	constructor(props: any) {
		super(props);

		this.state = {
			supplier: null,
			purchases: [],
			hasUnsavedFields: false,
			isNew: props.match?.params?.id === "new",
			name: "",
			contact: "",
			email: "",
			phone: "",
			address: "",
			notes: "",
			tab: "1",
			showPurchaseDrawer: false,
			selectedPurchase: null
		};
	}

	componentDidMount(): void {
		delayedDispatch(
			setBreadcrumb(() => {
				return {
					locations: [
						{
							text: Strings.sidebar.suppliers,
							route: "/suppliers",
							icon: "user-group"
						},
						{
							text: this.state.isNew ? Strings.suppliers.new : this.state.name || Strings.generic.loading
						}
					],
					actions: [
						{
							type: "button",
							text: Strings.generic.save,
							onClick: () => this.submitSupplier(),
							disabled: !this.state.hasUnsavedFields,
							className: this.state.hasUnsavedFields ? "BreadcrumbButtonSuccess" : "",
							isSave: true
						}
					]
				};
			})
		);

		this.getData();
	}

	componentDidUpdate(): void {
		const { dispatch } = this.props;

		dispatch(updateCrumb());
	}

	async getData() {
		const { isNew } = this.state;
		const { dispatch, match } = this.props;

		if (isNew) {
			return dispatch(setTitle(Strings.suppliers.new));
		}

		dispatch(setLoader(true));

		try {
			const [supplierResponse, purchasesResponse] = await Promise.all([
				API.get({
					url: Endpoints.uriSuppliers(match?.params?.id)
				}),
				API.get({
					url: Endpoints.uriWorkshopSupplierPurchases(match?.params?.id)
				})
			]);

			if (supplierResponse.ok) {
				const supplier = supplierResponse.data?.results?.supplier;
				dispatch(setTitle(supplier?.name || Strings.generic.loading));
				this.setState({
					supplier,
					name: supplier?.name || "",
					contact: supplier?.contact || "",
					email: supplier?.email || "",
					phone: supplier?.phone || "",
					address: supplier?.address || "",
					notes: supplier?.notes || "",
					...supplier
				});
			}

			if (purchasesResponse.ok) {
				this.setState({ purchases: purchasesResponse.data?.results?.purchases || [] });
			}
		} catch (err) {
			console.log("API Error", err);
			notification.error({
				message: Strings.serverErrors.title,
				description: (err as string) || Strings.serverErrors.wentWrong,
				placement: "bottomRight",
				duration: 5
			});
		}

		dispatch(setLoader(false));
	}

	async submitSupplier() {
		const { name, contact, email, phone, address, notes, isNew } = this.state;
		const { dispatch, match } = this.props;

		if (!name?.trim()) {
			return notification.warn({
				message: Strings.suppliers.supplier,
				description: Strings.errors.invalidFields,
				placement: "bottomRight",
				duration: 5
			});
		}

		if (email && !emailIsValid(email)) {
			return notification.warn({
				message: Strings.suppliers.supplier,
				description: Strings.errors.emailIsInvalid,
				placement: "bottomRight",
				duration: 5
			});
		}

		dispatch(setLoader(true));

		try {
			const request = !isNew ? API.put : API.post;
			const response = await request({
				url: Endpoints.uriSuppliers((!isNew && match?.params?.id) || ""),
				data: {
					name,
					contact,
					email,
					phone,
					address,
					notes
				}
			});

			if (response.ok && response.data.results?.supplier) {
				if (isNew) {
					return dispatch(push("/suppliers"));
				}

				const supplier = response.data.results.supplier;
				this.setState({
					hasUnsavedFields: false,
					supplier,
					name: supplier?.name || "",
					contact: supplier?.contact || "",
					email: supplier?.email || "",
					phone: supplier?.phone || "",
					address: supplier?.address || "",
					notes: supplier?.notes || "",
					...supplier
				});

				dispatch(setTitle(supplier?.name || ""));

				notification.success({
					message: Strings.suppliers.supplier,
					description: response.data.message || "",
					placement: "bottomRight",
					duration: 5
				});
			} else {
				notification.error({
					message: Strings.serverErrors.title,
					description: response?.data?.message || Strings.serverErrors.wentWrong,
					placement: "bottomRight",
					duration: 5
				});
			}
		} catch (err) {
			notification.error({
				message: Strings.serverErrors.title,
				description: (err as string) || Strings.serverErrors.wentWrong,
				placement: "bottomRight",
				duration: 5
			});
		}

		dispatch(setLoader(false));
	}

	renderPurchases() {
		const { purchases = [] } = this.state;

		return (
			<Table
				title={{
					icon: "shopping-cart",
					title: Strings.workshop.purchases
				}}
				data={purchases}
				columns={[
					{
						Header: Strings.workshop.date,
						id: "date",
						accessor: (row: any) =>
							row.date && DateTime.fromISO(row.date).toFormat("dd/MM/yyyy")
					},
					{
						Header: Strings.workshop.items,
						id: "items",
						accessor: (row: any) => (row.items?.length || 0) + " " + Strings.workshop.items
					},
					{
						Header: Strings.workshop.total,
						id: "total",
						align: "right",
						accessor: (row: any) => (row.total && `${row.total.toFixed(2)} €`) || "-"
					}
				]}
				filterable
				paginated={false}
				actions={{
					view: (original: any) => ({
						onClick: () => this.setState({ showPurchaseDrawer: true, selectedPurchase: original })
					})
				}}
			/>
		);
	}

	renderPurchaseDrawer() {
		const { showPurchaseDrawer, selectedPurchase } = this.state;

		if (!selectedPurchase) return null;

		return (
			<Drawer
				title={
					<div className="SidebarTitleContainer">
						<Icon name="shopping-cart" />
						<p>
							{Strings.workshop.purchase} -{" "}
							{selectedPurchase.date && DateTime.fromISO(selectedPurchase.date).toFormat("dd/MM/yyyy")}
						</p>
					</div>
				}
				footer={
					<div className="SidebarFooterContainer">
						<button
							type="button"
							className="SidebarFooterButton --button-cancel"
							onClick={() => this.setState({ showPurchaseDrawer: false, selectedPurchase: null })}
						>
							{Strings.generic.close}
						</button>
					</div>
				}
				placement="right"
				width={500}
				onClose={() => this.setState({ selectedPurchase: null, showPurchaseDrawer: false })}
				visible={showPurchaseDrawer}
			>
				<div style={{ marginBottom: 20 }}>
					<label className="InputLabel">{Strings.workshop.total}:</label>
					<div style={{ fontSize: 18, fontWeight: 700 }}>
						{selectedPurchase.total?.toFixed(2)} €
					</div>
				</div>
				<div>
					<label className="InputLabel">{Strings.workshop.items}:</label>
					<Table
						data={selectedPurchase.items || []}
						columns={[
							{
								Header: Strings.workshop.part,
								id: "part",
								accessor: (row: any) => translate(row.part?.name) || row.part?.partNumber || "-"
							},
							{
								Header: Strings.workshop.quantity,
								id: "quantity",
								accessor: (row: any) => row.quantity || 0
							},
							{
								Header: Strings.workshop.unitPrice,
								id: "unitPrice",
								align: "right",
								accessor: (row: any) => (row.unitPrice && `${row.unitPrice.toFixed(2)} €`) || "-"
							},
							{
								Header: Strings.workshop.total,
								id: "total",
								align: "right",
								accessor: (row: any) => (row.total && `${row.total.toFixed(2)} €`) || "-"
							}
						]}
						filterable={false}
						isSinglePage
						fullPage={false}
						paginated={false}
					/>
				</div>
			</Drawer>
		);
	}

	renderInfo() {
		const { name, contact, email, phone, address, notes } = this.state;
		const cnt = "PT";

		return (
			<ContentWrapper extraStyle={{ padding: 20 }}>
				<div className="WorksheetTitle" style={{ paddingBottom: 30 }}>
					<Icon name="paper-plane" />
					<p>{Strings.worksheets.generalInfo}</p>
				</div>
				<Row gutter={[20, 10]}>
					<Col xs={24} md={12} lg={6}>
						<label htmlFor="name" className="InputLabel --label-required">
							{Strings.fields.name}
						</label>
						<Input
							id="name"
							placeholder={Strings.fields.name}
							value={name || ""}
							onChange={(event: any) => {
								this.setState({ name: event.target.value, hasUnsavedFields: true });
							}}
						/>
					</Col>
					<Col xs={24} md={12} lg={6}>
						<label htmlFor="contact" className="InputLabel">
							{Strings.fields.name} ({Strings.suppliers.contacts})
						</label>
						<Input
							id="contact"
							placeholder={Strings.fields.name}
							value={contact || ""}
							onChange={(event: any) => {
								this.setState({ contact: event.target.value, hasUnsavedFields: true });
							}}
						/>
					</Col>
					<Col xs={24} md={12} lg={6}>
						<label htmlFor="email" className="InputLabel">
							{Strings.fields.email}
						</label>
						<Input
							id="email"
							type="email"
							placeholder={Strings.fields.email}
							value={email || ""}
							onChange={(event: any) => {
								this.setState({ email: event.target.value, hasUnsavedFields: true });
							}}
						/>
					</Col>
					<Col xs={24} md={12} lg={6}>
						<label htmlFor="phone" className="InputLabel">
							{Strings.fields.phone}
						</label>
						{Boolean(cnt) && (
							<PhoneInput
								ref={(ref: any) => {
									this.phoneValidationInput = ref;
								}}
								defaultCountry={cnt && cnt.toLowerCase()}
								value={phone || ""}
								inputClass="input-phone"
								onChange={(event: any) => {
									this.setState({ phone: event, hasUnsavedFields: true });
								}}
							/>
						)}
					</Col>
					<Col xs={24} md={12}>
						<label htmlFor="address" className="InputLabel">
							{Strings.fields.address}
						</label>
						<Input.TextArea
							id="address"
							placeholder={Strings.fields.address}
							value={address || ""}
							rows={3}
							onChange={(event: any) => {
								this.setState({ address: event.target.value, hasUnsavedFields: true });
							}}
						/>
					</Col>
					<Col xs={24} md={12}>
						<label htmlFor="notes" className="InputLabel">
							{Strings.fields.notes}
						</label>
						<Input.TextArea
							id="notes"
							placeholder={Strings.fields.notes}
							value={notes || ""}
							rows={4}
							onChange={(event: any) => {
								this.setState({ notes: event.target.value, hasUnsavedFields: true });
							}}
						/>
					</Col>
				</Row>
			</ContentWrapper>
		);
	}

	render(): JSX.Element {
		const { isNew, name } = this.state;

		return (
			<React.Fragment>
				<Helmet>
					<title>
						{Strings.suppliers.supplier} - {isNew ? Strings.suppliers.new : name || Strings.generic.loading}
					</title>
					<meta name="description" content="Description of Supplier" />
				</Helmet>
				<div className="WorksheetScreen" style={{ paddingBottom: 80 }}>
					{this.renderInfo()}
					{!isNew && (
						<ContentWrapper>
							{this.renderPurchases()}
						</ContentWrapper>
					)}
				</div>
				{this.renderPurchaseDrawer()}
			</React.Fragment>
		);
	}
}

const mapStateToProps = (state: any) => ({
	language: state.language
});

export default connect(mapStateToProps)(SupplierDetail);

